import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormContext, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Typography, Grid, Box, Button, Snackbar } from '@material-ui/core';
import MuiLink from '@material-ui/core/Link';
import { selectCommon } from '../../../store/common';
import UnathorizedLayout from '../../shared/layout/UnauthorizedLayout';
import FormInput from '../../shared/form/FormInput';
import FormPasswordInput from '../../shared/form/FormPasswordInput';
import { AlertContext } from '../../shared/app/AlertProvider';
import { IStatus, IError, cognitoErrorType, IUser } from '../../../types';
import { FormBox } from '../../shared/form-box';
import { useStyles } from './styles';
import AuthService from '../../../services/AuthService';
import useApi from '../../shared/hook/useApi';

const Login = () => {
  const [status, setStatus] = useState<IStatus>({ value: 'idle', code: null });
  const [email, setEmail] = useState('');
  const [isEmailUnverified, setIsEmailUnverified] = useState(false);
  const classes = useStyles();
  const { showAlert } = useContext(AlertContext);
  const { redirectUrl } = useSelector(selectCommon);
  const [t, i18n] = useTranslation();
  const history = useHistory();
  const [getAuthUser] = useApi<IUser>('getAuthUser');

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    submitFocusError: false,
  });

  const { handleSubmit } = methods;

  const onSubmit = useCallback(
    (data) => {
      const { email, password } = data;
      setEmail(email);
      setStatus({ value: 'pending', code: null });
      Auth.signIn(email, password)
        .then((result: any) => {
          const token = result.signInUserSession.accessToken.jwtToken;
          AuthService.setAuthInStorage(token, token);
          getAuthUser().then((user: IUser) => {
            if (window.analytics) {
              window.analytics.identify(user?.id, {
                language: i18n.language,
              });
            }
          });

          history.push({
            pathname: redirectUrl?.pathname ?? '/',
            search: redirectUrl?.search ?? '',
          });

          showAlert({
            severity: 'success',
            message: t('Login.successMsg'),
          });
        })
        .catch((e) => {
          if (e.code === cognitoErrorType.USER_NOT_CONFIRMED_EXCEPTION) {
            setIsEmailUnverified(true);
          } else if (
            e.code === cognitoErrorType.NOT_AUTHORIZED_EXCEPTION ||
            e.code === cognitoErrorType.USER_NOT_FOUND_EXCEPTION
          ) {
            showAlert({
              severity: 'error',
              message: t(
                'v2.global.snackbar_label_error_incorrectPasswordOrName',
              ),
            });
          } else {
            showAlert({
              severity: 'error',
              message: t('Login.notAllowed'),
            });
          }
          setStatus({ value: 'idle', code: null });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const resendEmail = () => {
    if (email != null) {
      Auth.resendSignUp(email)
        .then(() => {
          showAlert({
            severity: 'success',
            message: t(
              'v2.verification.snackbar_label_verificationEmailSentAgain',
            ),
          });
        })
        .catch((err: IError) => {
          showAlert({
            severity: 'error',
            message: t('v2.global.snackbar_label_error_somethingWentWrong'),
          });
        });
    }
  };

  const action = (
    <Button onClick={resendEmail} style={{ color: '#5CABFF' }}>
      {t('v2.global.snackbar_button_verification_resendVerificationEmail')}
    </Button>
  );

  return (
    <UnathorizedLayout
      title={t('v2.logIn.header_label_title')}
      subtitle={t('v2.logIn.header_label_subTitle')}
    >
      <FormContext {...methods}>
        <FormBox
          component="form"
          p={3}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {/* @ts-ignore next */}
              <FormInput
                id="login-email"
                type="email"
                name="email"
                variant="body1"
                label={t('v2.global.textField_label_companyEmail')}
                placeholder={t('v2.global.textField_placeholder_companyEmail')}
                required
              />
              {isEmailUnverified && (
                <p className={classes.errorMsg}>
                  {t(
                    'v2.global.textField_errorHandling_helperText_companyEmail_emailNotVerified',
                  )}
                </p>
              )}
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore next */}
              <FormPasswordInput
                id="login-password"
                name="password"
                variant="body1"
                label={t('v2.global.textField_label_password')}
                placeholder={t('v2.global.textField_placeholder_password')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box pt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={status.value === 'pending'}
                >
                  {t('v2.logIn.button_label_logIn')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_forgotPassword')}
              <MuiLink
                component={Link}
                to="/forgot-password"
                variant="body1"
                className={classes.links}
              >
                {t('v2.global.button_forgotPassword')}
              </MuiLink>
            </Grid>
            <Grid item xs={12}>
              {t('v2.global.label_createNewAccount')}
              <MuiLink
                component={Link}
                to="/registration"
                variant="body1"
                className={classes.links}
              >
                {t('v2.global.button_createNewAccount')}
              </MuiLink>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                {t('v2.global.label_needHelp')}
                <MuiLink
                  component="a"
                  variant="body1"
                  className={classes.links}
                  href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`}
                >
                  {t('v2.global.button_needHelp')}
                </MuiLink>
              </Typography>
            </Grid>
          </Grid>
        </FormBox>
      </FormContext>
      <Snackbar
        open={isEmailUnverified}
        onClose={() => setIsEmailUnverified(false)}
        message={t(
          'v2.global.snackbar_label_verification_resendVerificationEmail',
        )}
        action={action}
      />
    </UnathorizedLayout>
  );
};

export default Login;

type locationState = {
  email?: string;
};
